export const FIXED_DT = 1 / 60
const MAX_FRAME_DT = 0.25
const MAX_STEPS_PER_FRAME = 5

/**
 * Fixed-timestep accumulator.
 *
 * Simulation runs at exactly 60 Hz regardless of display refresh rate; the
 * leftover fraction becomes `alpha`, which the render systems use to blend
 * previous and current transforms. Without this, physics tuned on a 60 Hz
 * laptop would behave differently on a 144 Hz monitor.
 *
 * The step cap prevents the death spiral: if a frame takes so long that
 * catching up would take even longer, we deliberately let simulated time fall
 * behind rather than freezing the tab.
 */
export class Time {
  /**
   * Simulated seconds since start: does not advance while paused and runs slow
   * during hit-stop, because it accumulates the scaled delta. Use it for
   * animation phase, not for wall-clock measurements.
   */
  elapsed = 0
  /** Real delta of the last rendered frame, clamped. */
  frameDt = 0
  /** Same delta before `timeScale` is applied — for effects that ignore slow-mo. */
  rawDt = 0
  /** Interpolation factor into the current fixed step, 0..1. */
  alpha = 0
  /** Fixed steps executed during the last frame. */
  stepsLastFrame = 0
  /** Smoothed frames per second, for the debug overlay. */
  fps = 60
  paused = false
  /** Global simulation speed multiplier (slow-motion on knockouts). */
  timeScale = 1

  private accumulator = 0
  private lastTimestamp = 0
  private started = false

  /**
   * Advances the clock and returns how many fixed steps should run this frame.
   * `timestamp` comes straight from requestAnimationFrame.
   */
  beginFrame(timestamp: number): number {
    if (!this.started) {
      this.started = true
      this.lastTimestamp = timestamp
      this.stepsLastFrame = 0
      return 0
    }

    let dt = (timestamp - this.lastTimestamp) / 1000
    this.lastTimestamp = timestamp
    if (!Number.isFinite(dt) || dt < 0) dt = 0
    if (dt > MAX_FRAME_DT) dt = MAX_FRAME_DT

    this.fps += (1 / Math.max(dt, 1e-4) - this.fps) * 0.06
    this.rawDt = this.paused ? 0 : dt
    this.frameDt = this.paused ? 0 : dt * this.timeScale
    this.elapsed += this.frameDt

    this.accumulator += this.frameDt
    let steps = 0
    while (this.accumulator >= FIXED_DT && steps < MAX_STEPS_PER_FRAME) {
      this.accumulator -= FIXED_DT
      steps++
    }
    if (steps === MAX_STEPS_PER_FRAME) this.accumulator = 0

    this.alpha = this.accumulator / FIXED_DT
    this.stepsLastFrame = steps
    return steps
  }

  /** Call after unpausing or after a long stall so no huge catch-up happens. */
  resync(timestamp: number): void {
    this.lastTimestamp = timestamp
    this.accumulator = 0
    this.alpha = 0
  }
}
